import type { CustomerType } from '@today/types'
import { z } from 'zod'

/**
 * `POST /offers/recipients` — an inline prospect (TO-122 offer-recipient-selection).
 * At least one of `email` / `phone` is required; `marketingConsent` is an explicit,
 * required opt-in (no default). `.strict()` refuses every server-owned field — the
 * tenant, org unit and owner come from the DB-resolved principal only.
 */
export const offerRecipientCreateSchema = z
	.object({
		firstName: z.string().trim().min(1).max(100),
		lastName: z
			.string()
			.trim()
			.max(100)
			.nullish()
			.transform((value) => (value ? value : null)),
		email: z.email().max(320).optional(),
		phone: z.string().trim().min(1).max(32).optional(),
		marketingConsent: z.boolean(),
	})
	.strict()
	.refine((body) => body.email !== undefined || body.phone !== undefined, {
		message: 'email or phone is required',
		path: ['email'],
	})

export type OfferRecipientCreateInput = z.infer<typeof offerRecipientCreateSchema>
/** The request body as a client sends it (before parsing: `lastName` may be absent). */
export type OfferRecipientCreateBody = z.input<typeof offerRecipientCreateSchema>

/** A recipient candidate of the offer wizard's Step 1 (`GET`/`POST /offers/recipients`). */
export interface OfferRecipientDTO {
	id: string
	name: string
	orgUnitId: string
	type: CustomerType
	/** ISO-8601 UTC `createdAt` of the customer's latest non-deleted offer; null when none. */
	lastOfferDate: string | null
}
